import { useTranslation } from 'react-i18next';
import type { BaseFormSectionProps } from '../types/project-form-types';

/**
 * Resoluciones H3 disponibles para la malla canónica.
 * Rango relevante para análisis urbano: R7 (barrio grande) → R11 (parcela).
 */
const H3_RESOLUTIONS = [7, 8, 9, 10, 11] as const;
type H3Resolution = (typeof H3_RESOLUTIONS)[number];

/**
 * Descripción aproximada de cada resolución H3 en contexto urbano
 */
const RESOLUTION_LABELS: Record<H3Resolution, string> = {
  7: 'R7  (~5.2 km²)',
  8: 'R8  (~0.7 km²)',
  9: 'R9  (~0.1 km²)',
  10: 'R10 (~0.015 km²)',
  11: 'R11 (~0.002 km²)',
};

/**
 * Props para ProjectCanonicalGrid
 */
interface ProjectCanonicalGridProps extends BaseFormSectionProps {
  /** En modo edición el campo es de solo lectura: la R no puede cambiarse. */
  isEditMode: boolean;
}

/**
 * ProjectCanonicalGrid - Resolución de la malla canónica H3 del proyecto
 *
 * @remarks
 * Todo proyecto tiene exactamente una malla canónica H3, generada
 * automáticamente al momento de creación, inscrita dentro del área
 * de trabajo (bounding box territorial).
 *
 * El superadmin elige la resolución solo durante la creación.
 * Una vez creado el proyecto, la resolución no puede modificarse
 * y el botón de creación de mallas la muestra deshabilitado.
 *
 * **Cumple con:**
 * - P1: Componente < 200 líneas
 * - P3: Documentación JSDoc completa
 * - P6: Variables CSS (sin valores hardcoded)
 */
export function ProjectCanonicalGrid({
  formData,
  isLoading,
  isEditMode,
  onFormDataChange,
}: ProjectCanonicalGridProps): JSX.Element {
  const { t } = useTranslation('projects');

  /**
   * Actualiza la resolución H3 seleccionada
   *
   * @param value - Resolución como string (vendrá del select)
   */
  const handleResolutionChange = (value: string): void => {
    onFormDataChange({
      canonicalGridResolution: parseInt(value, 10),
    });
  };

  return (
    <div className="form-section">
      <h2 className="form-section-title-contract">{t('form.canonicalGrid')}</h2>

      <div className="form-group">
        <select
          value={formData.canonicalGridResolution}
          onChange={(e) => handleResolutionChange(e.target.value)}
          disabled={isLoading || isEditMode}
          className="form-input has-value"
          aria-label={t('form.canonicalGridResolutionLabel')}
        >
          {H3_RESOLUTIONS.map((r) => (
            <option key={r} value={r}>
              {RESOLUTION_LABELS[r]}
            </option>
          ))}
        </select>

        {isEditMode && (
          <small className="form-help">{t('form.canonicalGridReadOnly')}</small>
        )}
        {!isEditMode && (
          <small className="form-help">{t('form.canonicalGridHelp')}</small>
        )}
      </div>
    </div>
  );
}
